// catalogue: live from GET /plans, static BUNDLE_CATALOGUE as copy + fallback; Pro Tools isn't purchasable (priceMonthly: null).

import type { SvgIconComponent } from "@mui/icons-material";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import SsidChartRoundedIcon from "@mui/icons-material/SsidChartRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import StackedLineChartRoundedIcon from "@mui/icons-material/StackedLineChartRounded";
import TroubleshootRoundedIcon from "@mui/icons-material/TroubleshootRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import ViewModuleRoundedIcon from "@mui/icons-material/ViewModuleRounded";
import NewspaperRoundedIcon from "@mui/icons-material/NewspaperRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import KeyboardOptionKeyRoundedIcon from "@mui/icons-material/KeyboardOptionKeyRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import WaterfallChartRoundedIcon from "@mui/icons-material/WaterfallChartRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import LeaderboardRoundedIcon from "@mui/icons-material/LeaderboardRounded";
import CandlestickChartRoundedIcon from "@mui/icons-material/CandlestickChartRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";

export type BundleFeature = {
  name: string;
  description: string;
  isNew?: boolean;
  // Rendered in the "What's inside" section (icons match tgpt-proto /plans).
  Icon?: SvgIconComponent;
};

export type CatalogueBundle = {
  id: string;
  name: string;
  tagline: string;
  priceMonthly: number | null;
  features: BundleFeature[];
  // From `GET /plans` (absent on static entries, which derive/fall back):
  priceAnnual?: number | null;
  promptsPerDay?: number;
  itemPriceIds?: { monthly?: string; annual?: string };
};

// SPL v2 `GET /plans` response (data-bundle-pricing-migration.md, A.1)
export type ApiBundlePrice = {
  amount: number;
  item_price_id?: string;
  effective_monthly?: number;
};

export type ApiCatalogueBundle = {
  bundle_id: string;
  name: string;
  prompts_per_day: number;
  tagline?: string;
  features?: string[];
  price?: { monthly?: ApiBundlePrice; annual?: ApiBundlePrice };
};

export type ApiPlansCatalogue = {
  base?: { prompts?: number; dailyPrompts?: number; model?: string };
  bundles?: ApiCatalogueBundle[];
  pro_unlock?: { note?: string; tools?: string[] };
};

// Shipped GET /v2/plans shape (M1): prices in CENTS; normalizeApiBundle() bridges both shapes.
type SplItemPrice = { id?: string; price?: number };
type SplPlansBundle = {
  id: string;
  name?: string;
  dailyPrompts?: number;
  price_monthly?: SplItemPrice;
  price_annual?: SplItemPrice;
};

const priceFromItemPrice = (p?: SplItemPrice): ApiBundlePrice | undefined =>
  p ? { amount: (p.price ?? 0) / 100, item_price_id: p.id } : undefined;

const normalizeApiBundle = (raw: ApiCatalogueBundle | SplPlansBundle): ApiCatalogueBundle => {
  if ("bundle_id" in raw && raw.bundle_id) return raw;
  const live = raw as SplPlansBundle;
  return {
    bundle_id: live.id,
    name: live.name ?? live.id,
    prompts_per_day: live.dailyPrompts ?? 0,
    price: {
      monthly: priceFromItemPrice(live.price_monthly),
      annual: priceFromItemPrice(live.price_annual),
    },
  };
};

export type Catalogue = {
  bundles: CatalogueBundle[];
  basePrompts: number;
};

export const BASE_PROMPTS_PER_DAY = 5;
// Annual = 12 × monthly with 20% off ("Save 20%")
export const ANNUAL_DISCOUNT = 0.8;

export const BUNDLE_CATALOGUE: CatalogueBundle[] = [
  {
    id: "technical-indicator",
    name: "Technical Indicators",
    tagline: "Chart patterns, oscillators, and trend signals",
    priceMonthly: 2,
    features: [
      { name: "RSI & MACD", Icon: ShowChartRoundedIcon, isNew: true, description: "Momentum oscillators that signal overbought, oversold, and trend reversal conditions." },
      { name: "Bollinger Bands", Icon: SsidChartRoundedIcon, isNew: true, description: "Volatility bands around a moving average that highlight price extremes and compression breakouts." },
      { name: "Moving Averages", Icon: TimelineRoundedIcon, isNew: true, description: "Simple and exponential averages across configurable periods to track trend direction and support/resistance." },
      { name: "Pattern Recognition", Icon: AutoGraphRoundedIcon, isNew: true, description: "Automated detection of candlestick and chart patterns, including head-and-shoulders, double tops, and flags." },
    ],
  },
  {
    id: "market-data",
    name: "Market Data",
    tagline: "Real-time market movers and stock screening",
    priceMonthly: 5,
    features: [
      { name: "Top Gainers & Losers", Icon: TrendingUpRoundedIcon, description: "Stocks with the most significant price increases and decreases over a specified time frame." },
      { name: "Stock Screener (10K stocks)", Icon: TrackChangesRoundedIcon, description: "Filter stocks by market cap, sector, financial metrics, and custom criteria." },
      { name: "Stock Prices (OHLC)", Icon: StackedLineChartRoundedIcon, description: "Historical Open, High, Low, and Close prices for any ticker." },
      { name: "Insider Trading", Icon: TroubleshootRoundedIcon, description: "Buying and selling activity from executives, directors, and major shareholders." },
    ],
  },
  {
    id: "darkflow",
    name: "Darkflow",
    tagline: "Dark pool and institutional order flow",
    priceMonthly: 5,
    features: [
      { name: "Dark Pool Activity", Icon: VisibilityOffRoundedIcon, isNew: true, description: "Off-exchange trading volume and sentiment from dark pools, broken down by ticker." },
      { name: "Institutional Flow", Icon: AccountBalanceRoundedIcon, isNew: true, description: "Buy and sell activity from institutional investors, including fund-level positioning changes." },
      { name: "Large Block Trades", Icon: ViewModuleRoundedIcon, isNew: true, description: "High-value block transactions that indicate institutional conviction or liquidation." },
    ],
  },
  {
    id: "fundamentals",
    name: "Fundamentals",
    tagline: "Financials, earnings, and analyst coverage",
    priceMonthly: 5,
    features: [
      { name: "Company Financials", Icon: NewspaperRoundedIcon, description: "Income statements, balance sheets, cash flow statements, and key financial ratios." },
      { name: "Earnings Calendar", Icon: CalendarMonthRoundedIcon, description: "Schedule of upcoming and past earnings report dates for any company." },
      { name: "Analyst Estimates", Icon: InsightsRoundedIcon, description: "Forecasts and target prices from financial analysts on stock performance and earnings." },
      { name: "Super Investor Holdings", Icon: TipsAndUpdatesRoundedIcon, description: "Stock positions held by renowned investors and hedge funds." },
    ],
  },
  {
    id: "options",
    name: "Options Data",
    tagline: "Chains, Greeks, and unusual activity",
    priceMonthly: 10,
    features: [
      { name: "Options Chains & Greeks", Icon: KeyboardOptionKeyRoundedIcon, description: "Options contracts with strike prices, expiration dates, and Greeks for any ticker." },
      { name: "Put/Call Ratios", Icon: CompareArrowsRoundedIcon, isNew: true, description: "Aggregate and per-ticker put/call volume ratios for gauging sentiment and directional bias." },
      { name: "Unusual Options Activity", Icon: PriorityHighRoundedIcon, isNew: true, description: "Contracts with volume significantly above open interest, flagging potential informed trades." },
      { name: "Options Flow", Icon: WaterfallChartRoundedIcon, isNew: true, description: "Real-time stream of large options orders with strike, expiry, premium, and directional intent." },
    ],
  },
  {
    id: "pro-tools",
    name: "Pro Tools",
    tagline: "Pro model and advanced capabilities",
    priceMonthly: null,
    features: [
      { name: "Pro AI Model", Icon: PsychologyRoundedIcon, isNew: true, description: "Advanced reasoning model with deeper analysis and longer context for complex queries." },
      { name: "Advanced Charting", Icon: LeaderboardRoundedIcon, description: "Visualization of financial data, price movements, volume trends, and technical indicators." },
      { name: "Macroeconomic Data", Icon: CandlestickChartRoundedIcon, description: "GDP growth, interest rates, inflation, and their impact on U.S. stock markets." },
      { name: "Priority Queue", Icon: BoltRoundedIcon, isNew: true, description: "Dedicated processing lane with faster response times during peak market hours." },
    ],
  },
];

export const annualPrice = (monthly: number) =>
  Math.round(monthly * 12 * ANNUAL_DISCOUNT * 100) / 100;

// Annual prefers the API's explicit price; the formula is the static fallback.
export const bundlePrice = (bundle: CatalogueBundle, yearly: boolean) =>
  bundle.priceMonthly === null
    ? null
    : yearly
      ? bundle.priceAnnual ?? annualPrice(bundle.priceMonthly)
      : bundle.priceMonthly;

export const catalogueFromApi = (payload: unknown): Catalogue | null => {
  const api = payload as ApiPlansCatalogue | null | undefined;
  if (!api || !Array.isArray(api.bundles) || api.bundles.length === 0) return null;

  const bundles: CatalogueBundle[] = api.bundles.map((raw) => {
    const b = normalizeApiBundle(raw);
    const staticEntry = BUNDLE_CATALOGUE.find(s => s.id === b.bundle_id);
    return {
      id: b.bundle_id,
      name: b.name || staticEntry?.name || b.bundle_id,
      tagline: b.tagline || staticEntry?.tagline || "",
      priceMonthly: b.price?.monthly?.amount ?? staticEntry?.priceMonthly ?? 0,
      priceAnnual: b.price?.annual?.amount ?? null,
      promptsPerDay: b.prompts_per_day,
      itemPriceIds: {
        monthly: b.price?.monthly?.item_price_id,
        annual: b.price?.annual?.item_price_id,
      },
      features: staticEntry?.features
        ?? (b.features ?? []).map(name => ({ name, description: "" })),
    };
  });

  const proTools = BUNDLE_CATALOGUE.find(s => s.priceMonthly === null);
  if (proTools) bundles.push(proTools);

  return { bundles, basePrompts: api.base?.prompts ?? api.base?.dailyPrompts ?? BASE_PROMPTS_PER_DAY };
};

export const formatUsd = (amount: number) =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;

export const formatBundleNames = (bundles: CatalogueBundle[]) => {
  const names = bundles.map(b => b.name);
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

export type PlanSummary = {
  total: number;
  monthlyEquivalent: number;
  earnedPrompts: number;
  promptsPerDay: number;
};

export const summarizeSelection = (
  bundles: CatalogueBundle[],
  yearly: boolean,
  basePrompts: number = BASE_PROMPTS_PER_DAY,
): PlanSummary => {
  const total = Math.round(
    bundles.reduce((sum, b) => sum + (bundlePrice(b, yearly) ?? 0), 0) * 100,
  ) / 100;
  const earnedPrompts = bundles.reduce(
    (sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0,
  );
  return {
    total,
    monthlyEquivalent: yearly ? Math.round((total / 12) * 100) / 100 : total,
    earnedPrompts,
    promptsPerDay: basePrompts + earnedPrompts,
  };
};
